import { ReactElement, useState, useMemo } from 'react';
import SearchInput from '../controls/SearchInput';
import DeviceSelect from './devices/DeviceSelect';
import { DeviceType, ExtendedDeviceAttributes } from '../constants/telosair-interfaces';

type DeviceSelectionProps = {
  deviceDataArray: ExtendedDeviceAttributes[];
  handleSelect: (id: number, selectionStatus: boolean) => void;
  className?: string;
  deviceType: DeviceType;
};

/**
 * DeviceSelection renders the list of devices to be selected in a group.
 * @param props - Floor View Component Props.
 * @returns - DeviceSelection.
 */
export default function DeviceSelection(props: DeviceSelectionProps): ReactElement {
  const [searchText, setSearchText] = useState('');
  const { deviceDataArray, handleSelect, className, deviceType } = props;

  // Use useMemo to prevent unnecessary recalculations
  const filteredDevices = useMemo(() => {
    return deviceDataArray.filter((device) => !device.active);
  }, [deviceDataArray]);

  // Use useMemo for searched devices to prevent re-renders
  const searchedDevices = useMemo(() => {
    if (searchText) {
      return filteredDevices.filter((device) =>
        device.deviceName.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    return filteredDevices;
  }, [searchText, filteredDevices]);

  // Use useMemo for selected devices to prevent re-renders
  const selectedDevices = useMemo(() => {
    return deviceDataArray.filter((device) => device.active);
  }, [deviceDataArray]);

  return (
    <div className={className || 'py-5'}>
      {deviceType === DeviceType.INDOOR && selectedDevices.length > 0 && (
        <div className="pt-3 flex text-gray-500 dark:text-gray-500 text-sm">Selected Sensors</div>
      )}
      <div className="max-h-[17.5rem] overflow-y-auto mt-3">
        {deviceType === DeviceType.INDOOR &&
          selectedDevices.map((device) => (
            <DeviceSelect
              key={device.id}
              isNotSelected={false}
              device={device}
              handleSelect={handleSelect}
            />
          ))}
      </div>
      {filteredDevices.length > 0 && (
        <div className="mt-5 flex items-center space-x-4">
          <SearchInput textValue={searchText} setText={setSearchText} />
          {/* <Filter position="right" /> */}
        </div>
      )}

      {searchedDevices.length > 0 && (
        <div className="w-full max-h-[17.5rem] py-2 mt-5 overflow-auto rounded-lg border border-gray-700 flex-col justify-start items-start inline-flex">
          {searchedDevices.map((device) => (
            <DeviceSelect
              key={device.id}
              isNotSelected
              device={device}
              handleSelect={handleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
